/*!
 * image.js — pictures in a score, one file or a whole folder
 * © 2025 Rob Canning — GPLv3
 *
 * An <image> in the SVG is fine for a fixed picture, but a score often wants
 * a changing one: a folder of plates dealt out in a different order each
 * performance, a slideshow that advances while the playhead is inside a
 * region, a picture chosen by a pattern alongside the sounds it belongs with.
 *
 *   image(src:cloud.png)                       one file, shown when triggered
 *   image(path:plates, mode:shuffle)           a folder, a new one each pass
 *   image(src:Pseq(a.png, b.png, inf))         an explicit order
 *   image(path:plates, mode:seq, dur:2)        a slideshow, two beats a frame
 *
 * The cue element is the frame: whatever box it occupies is where the picture
 * goes, so you place and size it in Inkscape like anything else. The element
 * itself is hidden once the image is in place, the way other cues hide their
 * trigger.
 *
 * Selection reuses the same pattern engine as synth() and rotate(), so Pseq,
 * Prand, Pxrand, Pshuf and Pchoose all behave as they do everywhere else.
 *
 * A line of the live console has no element: its frame is made on the
 * stage, where at: and size: put it (stageFrame) --
 *   image(src:"https://…/sky.jpg")               the whole view
 *   image(uid:p, src:..., at:[0.75, 0.3], size:0.3)
 *   sh1 > image(src:...)                         in that shape's box
 *   image(uid:p, remove:1)  /  image(remove:all)  taken away, as shape()'s
 *   viz(p, blur:3, warp:20, hue:120)             the effects of a box, on the picture
 */

import { truthy } from "../parser/cueUtils.js";
import { makePatternGenerator } from "./audio/audioShared.js";
import { scoreSvg, shapesGroup, placeFrame } from "./shape.js";
import { atPair } from "../system/viewBand.js";
import { applyBoxFx } from "../system/boxFx.js";
import { bindParam, isSignalRef, isOscRef, publish } from "../control/paramBinding.js";
import { registerEscapeLayer } from "../system/keyLayers.js";
import { secs } from "./beats.js";
import { shuffle } from "./audio/patterns.js";
import { onReset } from "../system/teardown.js";
import { mapHtml, rebend, forgetHtml, onlyMap } from "../system/mapHandles.js";
import { mapText } from "../system/mapWarp.js";
import { listDir, findFile, urlOf, filterPool, missNote } from "../system/mediaFolder.js";

const SVG_NS = "http://www.w3.org/2000/svg";

/** uid → { files, cursor, order, gen } so cues sharing a uid share a position */
const pools = new Map();
/** element → live state, so a re-trigger replaces rather than stacks */
const live = new Map();
/** url → Promise, so a file is only fetched once */
const preloaded = new Map();
// another folder picked on this computer (mediaFolder.js): its lists and files afresh
window.addEventListener("oscilla:media-folder", () => { pools.clear(); preloaded.clear(); });

// ── file lists ───────────────────────────────────────────────────────────
async function listFolder(project, folder) {
  const local = listDir(folder ?? "", "images");
  if (local) return local;
  const url = `/api/image-list/${encodeURIComponent(project)}` +
              (folder ? "/" + folder.split("/").map(encodeURIComponent).join("/") : "");
  try {
    const r = await fetch(url);
    if (!r.ok) throw new Error(`${r.status}`);
    const d = await r.json();
    return Array.isArray(d.files) ? d.files : [];
  } catch (err) {
    console.warn(`[cue:image] could not list "${folder || "images/"}": ${err.message}`);
    return [];
  }
}

function urlFor(file, folder) {
  if (/^(https?:)?\/\//.test(file) || file.startsWith("/")) return file;   // absolute, as given
  const local = findFile(folder ? `${folder}/${file}` : file, "images");
  if (local) return urlOf(local);
  const base = window.imageDir || "images/";
  return folder ? `${base}${folder}/${file}` : `${base}${file}`;
}

/** Fetch once and keep it, so the picture is ready before the playhead arrives. */
function preload(url) {
  if (preloaded.has(url)) return preloaded.get(url);
  const p = new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(url);
    img.onerror = () => { console.warn(`[cue:image] missing: ${url}${missNote(url.replace(window.imageDir || "images/", ""), "images")}`); resolve(null); };
    img.src = url;
  });
  preloaded.set(url, p);
  return p;
}

// ── which file next ──────────────────────────────────────────────────────

/**
 * mode: seq (in order, wrapping) · shuffle (a fresh order each time round,
 * never the same file twice within a pass) · random (independent each time,
 * repeats possible) · once (stop at the end).
 */
function nextFile(pool, mode) {
  const { files } = pool;
  if (!files.length) return null;
  if (mode === "random") return files[Math.floor(Math.random() * files.length)];

  if (pool.cursor >= pool.order.length) {
    if (mode === "once") return null;
    pool.order = mode === "shuffle" ? shuffle(files) : [...files];
    pool.cursor = 0;
  }
  return pool.order[pool.cursor++];
}

// ── fullscreen ───────────────────────────────────────────────────────────
// A picture over the whole viewport rather than inside the score: a plate to
// read from, a projection, an interruption. It leaves the score scrolling
// underneath, so the playhead keeps its place while the picture is up.
//
// Layering, in the same band the video cue uses:
//   backdrop 9998 · picture 9999 · close button 10000
// layer:under puts the picture beneath the score instead, for a ground the
// notation is read against.
const _openFullscreen = [];          // dismiss functions, newest last
let _escBound = false;

function bindEscape() {
  if (_escBound) return;
  _escBound = true;
  // Same tier as a fullscreen video: it covers everything, so Escape takes it
  // first, one press per picture.
  registerEscapeLayer({
    id: "cue-image-fullscreen",
    tier: "overlay",
    isActive: () => _openFullscreen.length > 0,
    dismiss: () => _openFullscreen[_openFullscreen.length - 1]?.(),
  });
}

function showFullscreen(url, opts) {
  bindEscape();

  const under = opts.layer === "under";
  let backdrop = null;
  if (opts.backdrop && !under) {
    backdrop = document.createElement("div");
    backdrop.className = "oscilla-image-backdrop";
    backdrop.style.cssText =
      "position:fixed;inset:0;z-index:9998;background:rgba(0,0,0,0.88);" +
      `opacity:0;transition:opacity ${opts.fadeIn || 0.2}s linear;`;
    document.body.appendChild(backdrop);
  }

  const img = document.createElement("img");
  img.className = "oscilla-image-fullscreen";
  img.dataset.imageUid = opts.uid;
  img.src = url;
  img.alt = "";
  img.style.cssText =
    `position:fixed;inset:0;margin:auto;max-width:100vw;max-height:100vh;` +
    `width:${opts.fit === "fill" ? "100vw" : "auto"};` +
    `height:${opts.fit === "fill" ? "100vh" : "auto"};` +
    (opts.fit === "cover" ? "width:100vw;height:100vh;object-fit:cover;" : "") +
    `z-index:${under ? 1 : 9999};pointer-events:${opts.clickToClose ? "auto" : "none"};` +
    `cursor:${opts.clickToClose ? "pointer" : "default"};` +
    `opacity:0;transition:opacity ${opts.fadeIn || 0.2}s linear;`;
  // in a box of its own, the picture's place on the page: a page that turns
  // its colours (Live's dark stage, live.css) turns this back, while
  // viz()'s effects go on the picture inside it
  const box = document.createElement("div");
  box.className = "oscilla-image-fullscreen-box";
  box.style.cssText = `position:fixed;inset:0;z-index:${under ? 1 : 9999};pointer-events:none;`;
  box.appendChild(img);
  document.body.appendChild(box);

  let closeBtn = null;
  if (opts.closeBtn && !under) {
    closeBtn = document.createElement("button");
    closeBtn.textContent = "×";
    closeBtn.title = "Close (Esc)";
    closeBtn.style.cssText =
      "position:fixed;top:14px;right:16px;z-index:10000;width:40px;height:40px;" +
      "border-radius:20px;border:none;background:rgba(0,0,0,0.55);color:#fff;" +
      "font-size:24px;line-height:1;cursor:pointer;";
    document.body.appendChild(closeBtn);
  }

  requestAnimationFrame(() => {
    img.style.opacity = String(opts.opacity ?? 1);
    if (backdrop) backdrop.style.opacity = "1";
  });

  let done = false;
  const dismiss = () => {
    if (done) return;
    done = true;
    const i = _openFullscreen.indexOf(dismiss);
    if (i >= 0) _openFullscreen.splice(i, 1);
    const out = opts.fadeOut || 0;
    for (const n of [img, backdrop, closeBtn]) {
      if (!n) continue;
      if (out > 0) { n.style.transition = `opacity ${out}s linear`; n.style.opacity = "0"; }
    }
    setTimeout(() => { box.remove(); backdrop?.remove(); closeBtn?.remove(); }, out * 1000 + 60);
  };

  closeBtn?.addEventListener("click", dismiss);
  if (opts.clickToClose) img.addEventListener("click", dismiss);
  _openFullscreen.push(dismiss);
  return { node: img, dismiss };
}

// ── placement ────────────────────────────────────────────────────────────

// What a frame shows sits in a group of its own just after it, tagged with
// the uid: viz(p, blur:3)'s effects go on that group (viz.js), so they stay
// on as a slideshow changes the picture.
const holders = new WeakMap();          // frame -> <g>
let holderSeq = 0;
function holderOf(el, uid) {
  let g = holders.get(el);
  if (!g?.isConnected) {
    g = document.createElementNS(SVG_NS, "g");
    g.setAttribute("class", "oscilla-image-holder");
    g.id = `image-holder-${++holderSeq}`;
    el.parentNode.insertBefore(g, el.nextSibling);
    holders.set(el, g);
  }
  g.dataset.imageUid = uid;
  return g;
}

/** The effects viz() gave this uid, laid on what it shows now (a new picture of a slideshow). */
function relayFx(uid) {
  for (const g of document.querySelectorAll(`.oscilla-image-holder[data-image-uid="${CSS.escape(uid)}"]`)) applyBoxFx(g);
}
const FIT = { contain: "xMidYMid meet", cover: "xMidYMid slice", fill: "none" };

function placeImage(el, url, opts) {
  const svg = el.ownerSVGElement;
  if (!svg) return null;
  let box;
  try { box = el.getBBox(); } catch { return null; }
  if (!box || !box.width || !box.height) {
    console.warn("[cue:image] the cue element has no size — give it a rect to sit in");
    return null;
  }
  if (bent.has(opts.uid)) return placeBent(el, url, box, opts);

  const node = document.createElementNS(SVG_NS, "image");
  node.setAttributeNS(null, "x", box.x);
  node.setAttributeNS(null, "y", box.y);
  node.setAttributeNS(null, "width", box.width);
  node.setAttributeNS(null, "height", box.height);
  node.setAttributeNS(null, "preserveAspectRatio", FIT[opts.fit] ?? FIT.contain);
  node.setAttribute("href", url);
  node.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", url);  // older renderers
  node.setAttribute("class", "oscilla-image");
  node.style.pointerEvents = "none";
  node.style.opacity = opts.fadeIn > 0 ? "0" : String(opts.opacity);
  if (opts.fadeIn > 0) node.style.transition = `opacity ${opts.fadeIn}s linear`;

  holderOf(el, opts.uid).appendChild(node);
  if (opts.fadeIn > 0) requestAnimationFrame(() => { node.style.opacity = String(opts.opacity); });
  return node;
}

// ── map: -- a picture bent onto a surface (mapHandles.js) ────────────────
//
// An SVG <image> takes no 3D transform, so a mapped picture is an <img> in
// a foreignObject over its frame, bent by CSS. The same fit for each picture
// of a slideshow.

const bent = new Set();                 // uids with map:
const BENT_FIT = { contain: "contain", cover: "cover", fill: "fill" };

function placeBent(el, url, box, opts) {
  const fo = document.createElementNS(SVG_NS, "foreignObject");
  for (const [k, v] of [["x", box.x], ["y", box.y], ["width", box.width], ["height", box.height]]) fo.setAttribute(k, v);
  fo.setAttribute("class", "oscilla-image");
  fo.style.pointerEvents = "none";
  fo.style.overflow = "visible";
  const img = document.createElement("img");
  img.className = "oscilla-image-bent";
  img.src = url;
  img.style.cssText = `display:block;width:100%;height:100%;object-fit:${BENT_FIT[opts.fit] ?? "contain"}`;
  fo.appendChild(img);
  fo.style.opacity = opts.fadeIn > 0 ? "0" : String(opts.opacity);
  if (opts.fadeIn > 0) fo.style.transition = `opacity ${opts.fadeIn}s linear`;
  holderOf(el, opts.uid).appendChild(fo);
  if (opts.fadeIn > 0) requestAnimationFrame(() => { fo.style.opacity = String(opts.opacity); });
  rebend(`image:${opts.uid}`);
  return fo;
}

/** map: on uid's pictures: 1, [[x, y] ...], 0. `frame`: what it is shown in, where map:1's corners start. */
function imageMap(uid, v, frame) {
  const on = mapHtml(`image:${uid}`, v, {
    flat: () => frame.getBoundingClientRect(),
    targets: () => [...document.querySelectorAll(`.oscilla-image-holder[data-image-uid="${CSS.escape(uid)}"] img.oscilla-image-bent`)].map(el => ({ el })),
    line: pts => `image(uid:${uid}, map:${mapText(pts)})`,
    write: { uid, cues: ["image"] },
  });
  if (on) bent.add(uid); else bent.delete(uid);
}

function removeImage(node, fadeOut) {
  if (!node) return;
  if (fadeOut > 0) {
    node.style.transition = `opacity ${fadeOut}s linear`;
    node.style.opacity = "0";
    setTimeout(() => node.remove(), fadeOut * 1000 + 60);
  } else {
    node.remove();
  }
}

// ── a frame on the stage, for a line with no element ─────────────────────

/**
 * The frame a console line's picture sits in: a rect on the stage, as a
 * shape is placed -- at: 0..1 of the view (the middle by default), size: a
 * fraction of the view across and down (1, the default, all of it; [w, h]
 * apart). One for each uid, so the line run again shows in the same frame,
 * or moves it there.
 */
const stageFrames = new Map();          // uid -> rect
function stageFrame(p) {
  const svg = scoreSvg();
  if (!svg) return null;
  const group = shapesGroup(svg);
  const uid = String(p.uid ?? "image");
  let rect = stageFrames.get(uid);
  if (!rect?.isConnected) {
    rect = document.createElementNS(SVG_NS, "rect");
    rect.id = `image-frame-${uid}`;
    group.appendChild(rect);
    stageFrames.set(uid, rect);
  }
  return placeFrame(rect, group, atPair(p.at), p.size) ? rect : null;
}

// ── opacity as a live connection ───────────────────────────────────────────
// image(opacity: follow(fader.t)) lets anything publishing a 0-1 signal dim the
// picture. The binding is set up when the cue is SCANNED, not when it fires:
// a connection is a property of the score, so dragging the source must move
// the placeholder too, and a playhead scrubbed back past the cue must not
// sever it. handleImageCue and primeImage both call bindOpacity; binding is
// owned by "image:<uid>:opacity", so whichever runs second replaces rather
// than stacks.
const _opacity = new WeakMap();   // el → current bound value

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v)));

function imageUid(ast, el) {
  const u = (ast?.args || []).find(a => a?.type === "uid")?.value;
  return String(u ?? el?.getAttribute?.("id") ?? "image");
}

/** Push the current value onto whichever picture node this element is showing. */
function applyOpacityTo(el) {
  const n = live.get(el)?.node;
  const v = _opacity.get(el);
  if (n && v !== undefined) n.style.opacity = String(v);
}

/** The value to draw with now: the bound one if there is one, else the literal. */
function currentOpacity(el, literal) {
  const bound = _opacity.get(el);
  if (bound !== undefined) return bound;
  return literal != null ? clamp01(literal) : 1;
}

/** Bind opacity if the cue asked for one. Returns the ref, or null. */
function bindOpacity(ast, el) {
  const arg = (ast?.args || []).find(a => a?.type === "opacity")?.value;
  if (!isSignalRef(arg) && !isOscRef(arg)) return null;
  const binding = bindParam(arg, (v) => {
    _opacity.set(el, clamp01(v));
    applyOpacityTo(el);
  }, { min: 0, max: 1, owner: `image:${imageUid(ast, el)}:opacity` });
  // bindParam replays whatever the signal already holds, so seed from it.
  _opacity.set(el, clamp01(binding.value));
  return arg;
}

// ── the cue ──────────────────────────────────────────────────────────────

export async function handleImageCue(ast, el, options = {}) {
  if (!ast) return;

  const p = {};
  for (const a of (ast.args || [])) if (a?.type) p[a.type] = a.value;
  // a line of the live console: a frame on the stage (size:fullscreen keeps
  // it only as the picture's place in `live`)
  // image(uid:p, remove:1): that one taken away; remove:all every one (as shape())
  if (p.remove != null && p.remove !== 0 && p.remove !== "0") {
    removeImages(String(p.remove).toLowerCase() === "all" ? null : String(p.uid ?? "image"));
    return;
  }
  // map:[...] alone -- a fit dragged on the laptop, run by the projector: only that
  if (onlyMap(ast.args || [])) {
    const u = imageUid(ast, el), frame = el ?? stageFrames.get(u);
    if (frame) imageMap(u, p.map, frame);
    return;
  }
  el ??= stageFrame(p);
  if (!el) return;
  // map: -- bent onto a surface; the pictures placed from now on are <img>s to bend
  if (p.map != null) {
    const u = imageUid(ast, el);
    if (!/^(0|off|false|none)$/.test(String(p.map))) bent.add(u);
    imageMap(u, p.map, el);
  }

  const project = window.currentProject;
  const folder  = p.path != null ? String(p.path).replace(/^\/+|\/+$/g, "") : null;
  const mode    = String(p.mode ?? "shuffle").toLowerCase();
  const fit     = String(p.fit ?? "contain").toLowerCase();
  const opacityRef = bindOpacity(ast, el);
  let opacity = currentOpacity(el, p.opacity);
  // fades, dur and hold are written in beats; from here on they are seconds
  const fadeIn  = Number(secs(p.fadein ?? p.fadeIn ?? p.fade ?? 0)) || 0;
  const fadeOut = Number(secs(p.fadeout ?? p.fadeOut ?? p.fade ?? 0)) || 0;
  const dur     = p.dur != null ? Number(secs(p.dur)) : null;   // slideshow step, and how long it stays
  const uid     = imageUid(ast, el);
  const sizeStr = String(p.size ?? "").toLowerCase();
  const full    = sizeStr === "fullscreen" || sizeStr === "fs";
  const layer   = String(p.layer ?? "over").toLowerCase();
  const backdrop = p.backdrop != null
    ? !(p.backdrop === 0 || p.backdrop === "0" || p.backdrop === false)
    : true;                                    // a fullscreen picture dims the score by default
  const closeBtn = p.close === 1 || p.close === "1" || p.close === true;
  const cc = p.clickclose ?? p.clickClose;
  const clickToClose = truthy(cc);
  const hold    = p.hold != null ? Number(secs(p.hold)) : null;   // beats up, then it goes
  const glob    = p.glob != null ? String(p.glob) : null;

  // ── where the files come from ──────────────────────────────────────────
  let pool = pools.get(uid);
  if (!pool || pool.folder !== folder || pool.srcKey !== JSON.stringify(p.src ?? null)) {
    let files = [];
    let gen = null;
    if (p.src != null && typeof p.src === "object" && p.src.values) {
      gen = makePatternGenerator(p.src);                  // image(src:Pseq(...))
    } else if (p.src != null) {
      files = [String(p.src)];                            // image(src:one.png)
    } else if (folder != null) {
      files = await listFolder(project, folder);          // image(path:folder)
      files = filterPool(files, { glob });
      if (!files.length) console.warn(`[cue:image] nothing in "${folder}" for ${uid}`);
    } else {
      console.warn("[cue:image] needs src: or path:");
      return;
    }
    pool = { files, folder, srcKey: JSON.stringify(p.src ?? null), order: [], cursor: 0, gen };
    pools.set(uid, pool);
    for (const f of files.slice(0, 40)) preload(urlFor(f, folder));   // ready before the playhead
  }

  // ── stop anything this element is already showing ──────────────────────
  const prev = live.get(el);
  if (prev) {                      // including a placeholder put up at load
    clearInterval(prev.timer);
    clearTimeout(prev.hideTimer);
    if (prev.dismiss) prev.dismiss(); else removeImage(prev.node, 0);
    live.delete(el);
  }

  const state = { node: null, timer: null, hideTimer: null, dismiss: null, placeholder: false, uid };
  live.set(el, state);
  // Read synchronously by the dispatcher's scrub-back check. triggeredCues is
  // not usable there: the rewind button clears it before the playhead moves,
  // so "has this fired?" is already false by the time we could ask.
  el._oscillaImageRunning = true;

  const applyOpacity = () => { opacity = currentOpacity(el, p.opacity); applyOpacityTo(el); };

  const showFile = async (file) => {
    // Which plate this is, so a score can follow the slideshow: index is the
    // position in the folder, t the same thing as 0-1. A Pseq of explicit
    // names has no folder to be a position in, hence indexOf rather than a
    // counter — it reports -1 and publishes nothing, which is honest.
    const idx = pool.files.indexOf(String(file));
    if (idx >= 0) {
      publish("image", uid, {
        index: idx,
        t: pool.files.length > 1 ? idx / (pool.files.length - 1) : 0,
      });
    }
    const url = urlFor(String(file), folder);
    await preload(url);
    if (full) {
      state.dismiss?.();
      holderOf(el, uid);           // empty: what viz(uid, ...) finds; its effects go on the picture
      const fs = showFullscreen(url, { fit, opacity, fadeIn, fadeOut, backdrop, closeBtn, clickToClose, layer, uid });
      state.node = fs.node;
      state.dismiss = fs.dismiss;
    } else {
      removeImage(state.node, fadeOut);
      state.node = placeImage(el, url, { fit, opacity, fadeIn, uid });
      if (opacityRef) applyOpacity();
    }
    relayFx(uid);
  };

  const show = async () => {
    const file = pool.gen ? pool.gen.next() : nextFile(pool, mode);
    if (file == null) { clearInterval(state.timer); return; }
    await showFile(file);
  };

  // The element is the frame, not the picture. An SVG rect with no fill
  // attribute paints black, so make it transparent rather than merely hidden:
  // a click-triggered cue needs it left in the hit-test, sitting under the
  // picture (which takes no pointer events) so presses reach it.
  const clickable = String(p.trig ?? "").toLowerCase() === "click";
  el.style.fill = "transparent";
  el.style.stroke = "none";
  if (clickable) {
    el.style.visibility = "";
    el.style.pointerEvents = "all";
    el.style.cursor = "pointer";
  } else {
    el.style.visibility = "hidden";
  }

  // ── index: as an inlet, the picture is chosen rather than dealt ─────────
  // A folder stops being a slideshow and becomes a lookup table: a rotation's
  // angle, a fader, an incoming OSC value picks the plate. dur is ignored
  // while this is bound — something else is doing the advancing.
  const indexArg = (ast.args || []).find(a => a?.type === "index")?.value;
  const indexBound = isSignalRef(indexArg) || isOscRef(indexArg);

  if (indexBound) {
    const last = pool.files.length - 1;
    if (last < 0) {
      console.warn(`[cue:image] ${uid}: index: needs a path: folder or a list of files to index into`);
    } else {
      let shown = -1;
      const showIndex = (v) => {
        if (!Number.isFinite(v)) return;
        const i = Math.min(last, Math.max(0, Math.round(v)));
        if (i === shown) return;          // the same plate, don't reload it
        shown = i;
        showFile(pool.files[i]);
      };
      // Range defaults to the folder, so follow(fx.t) with no numbers spans it.
      const binding = bindParam(indexArg, showIndex,
        { min: 0, max: last, owner: `image:${uid}:index` });
      // bindParam replays what the signal already holds; if it holds nothing
      // yet, start at the first plate rather than an empty frame.
      if (shown < 0) showIndex(Number.isFinite(binding.value) ? binding.value : 0);
    }
  } else {
    await show();
  }

  // dur steps a slideshow; hold ends the whole cue. A single picture with a
  // dur and nothing to advance to is treated as a hold, which is what anyone
  // writing image(src:x.png, dur:4) means.
  const single = !pool.gen && pool.files.length <= 1;
  if (dur != null && dur > 0 && !single && !indexBound) {
    state.timer = setInterval(show, dur * 1000);
  }
  const endAfter = hold != null && hold > 0 ? hold : (single && dur > 0 ? dur : null);
  if (endAfter != null) {
    state.hideTimer = setTimeout(() => {
      clearInterval(state.timer);
      if (state.dismiss) state.dismiss(); else removeImage(state.node, fadeOut);
      state.node = null; state.dismiss = null;
      el._oscillaImageRunning = false;          // ran its course on its own
    }, endAfter * 1000);
  }
}

/**
 * Stop whatever this element is showing and put its placeholder back.
 *
 * Used when the playhead is scrubbed back to before a trig:playhead image
 * cue: a slideshow should not keep dealing frames behind a playhead that has
 * not reached it, and the box should read as "not yet" again rather than
 * holding whichever frame it happened to stop on.
 */
export function resetImage(el, ast = null) {
  // The opacity binding is deliberately NOT released here. Scrubbing the
  // playhead back past a picture resets the picture, not the score's wiring —
  // releasing it would leave the image permanently deaf to its source.
  const st = live.get(el);
  const wasRunning = el?._oscillaImageRunning === true;
  if (el) el._oscillaImageRunning = false;
  if (st) {
    clearInterval(st.timer);
    clearTimeout(st.hideTimer);
    if (st.dismiss) st.dismiss(); else removeImage(st.node, 0);
    live.delete(el);
  }
  // start the folder again, so the next pass deals from the top
  const uid = imageUid(ast, el);
  const pool = pools.get(uid);
  if (pool) { pool.cursor = 0; pool.order = []; }
  if (ast) primeImage(ast, el);
  return (st || wasRunning) ? 1 : 0;
}


/**
 * Show the first picture before the cue has fired, so a score at rest reads as
 * the score rather than as a row of empty rectangles. Called once per element
 * when the cues are scanned, the way fade() primes its target.
 *
 * Skipped for fullscreen cues: a placeholder covering the window would be
 * worse than a blank box, not better.
 */
export async function primeImage(ast, el) {
  if (!ast || !el) return;
  const p = {};
  for (const a of (ast.args || [])) if (a?.type) p[a.type] = a.value;

  const raw = p.placeholder ?? p.placehold ?? 1;
  if (raw === 0 || raw === "0" || raw === false) return;          // placeholder:0
  const sizeStr = String(p.size ?? "").toLowerCase();
  if (sizeStr === "fullscreen" || sizeStr === "fs") return;
  if (live.has(el)) return;                                       // already showing something

  const folder = p.path != null ? String(p.path).replace(/^\/+|\/+$/g, "") : null;
  let first = null;
  if (p.src != null && typeof p.src === "object" && p.src.values) {
    const v = p.src.values[0];
    first = Array.isArray(v) ? v[0] : v;                          // Pseq(a.png, …)
  } else if (p.src != null) {
    first = String(p.src);
  } else if (folder != null) {
    const files = await listFolder(window.currentProject, folder);
    first = files[0] ?? null;
  }
  if (first == null) return;
  if (live.has(el)) return;                                       // triggered while listing

  const url = urlFor(String(first), folder);
  await preload(url);
  if (live.has(el)) return;

  bindOpacity(ast, el);
  const placeholderAlpha = Number(raw) > 0 && Number(raw) <= 1 ? Number(raw) : 1;
  const alpha = currentOpacity(el, placeholderAlpha);
  el.style.fill = "transparent";
  el.style.stroke = "none";
  const node = placeImage(el, url, {
    fit: String(p.fit ?? "contain").toLowerCase(),
    opacity: alpha,
    fadeIn: 0,
  });
  if (!node) return;
  node.dataset.placeholder = "1";
  el.style.visibility = "hidden";
  live.set(el, { node, timer: null, hideTimer: null, dismiss: null, placeholder: true });
  applyOpacityTo(el);              // a value may have arrived while listing/preloading
}

/**
 * The pictures of `uid`, or every one (null: image(remove:all), and a
 * project switch): slideshows stopped, frames and holders gone, a
 * fullscreen one closed.
 */
export function removeImages(uid) {
  if (uid == null) {
    while (_openFullscreen.length) _openFullscreen[_openFullscreen.length - 1]();
    document.querySelectorAll(".oscilla-image-fullscreen-box, .oscilla-image-backdrop, image.oscilla-image").forEach(n => n.remove());
  }
  for (const [el, st] of live) {
    if (uid != null && st.uid !== uid && el.id !== `image-frame-${uid}`) continue;
    clearInterval(st.timer);
    clearTimeout(st.hideTimer);
    if (st.dismiss) st.dismiss(); else removeImage(st.node, 0);
    el._oscillaImageRunning = false;
    live.delete(el);
  }
  for (const u of [...bent]) if (uid == null || u === uid) { forgetHtml(`image:${u}`); bent.delete(u); }
  for (const [u, rect] of stageFrames) {
    if (uid != null && u !== uid) continue;
    rect.remove();
    stageFrames.delete(u);
  }
  for (const g of document.querySelectorAll(".oscilla-image-holder")) {
    if (uid == null || g.dataset.imageUid === uid) g.remove();
  }
  if (uid == null) pools.clear(); else pools.delete(uid);
}
onReset("project", function removeAllImages() { removeImages(null); });   // slideshows stopped, a fullscreen one closed

/** Take every picture off the score — project switch, rewind to start. */
